import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideEnvironmentInitializer,
} from '@angular/core';
import { provideRouter, withRouterConfig } from '@angular/router';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { routes } from './app.routes';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { provideAppIcons } from './core/icons/icon-registry.provider';
import { ThemeService } from './core/theme/theme.service';
import { I18nService } from './core/config/i18n/i18n.service';
import { apiErrorInterceptor } from './core/auth/api-error.interceptor';
import { projectContextInterceptor } from './core/project-context/project-context.interceptor';
import { authHeaderInterceptor } from './core/auth/auth-header.interceptor';
import { AuthStore } from './core/auth/auth.store';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarConfig } from '@angular/material/snack-bar';
import { SNACKBAR_DURATION } from '@shared/global/globals';

/**
 * Router setup, exported so the route tests exercise the real configuration.
 *
 * `onSameUrlNavigation: 'reload'` is what lets `PermissionsStore` re-run the
 * guards for the page the user is on after a project switch; on its own the
 * router would treat that same-URL navigation as a no-op. The guarded routes
 * pair it with `runGuardsAndResolvers: 'always'`.
 */
export const appRouterProviders = provideRouter(
  routes,
  withRouterConfig({ onSameUrlNavigation: 'reload' }),
);

/**
 * Snackbar defaults, exported so the snackbar tests exercise the real
 * configuration. Every notification is opened with these — call sites only
 * pass `data`.
 *
 * `duration` is what dismisses the snackbar; `SnackbarData.duration` is what
 * paces the countdown bar the custom snackbar renders. A call site overriding
 * one has to override the other too, or the bar desynchronizes from the
 * dismissal.
 */
export const snackbarDefaultOptions: MatSnackBarConfig = {
  duration: SNACKBAR_DURATION,
  horizontalPosition: 'right',
  verticalPosition: 'top',
  panelClass: 'custom-snackbar',
};

/**
 * Modal defaults, exported so the modal tests exercise the real configuration.
 *
 * `panelClass` is the contract with `.sci-modal-panel` in `_theme.scss`, which
 * empties out the Material surface so `sci-modal` can draw the box itself. A
 * call site passing its own `panelClass` *replaces* this one and loses that
 * reset — which is why `ModalService`, not the call site, owns the config.
 * `backdropClass` works the same way, and replaces CDK's
 * `cdk-overlay-dark-backdrop` with the themed `--modal-backdrop`.
 *
 * `width` is deliberately unset: the overlay pane stays `width: auto` and takes
 * its size from `sci-modal`'s `size` input, so the full-screen degrade below
 * the tablet breakpoint is a plain media query in the component's own styles.
 *
 * `disableClose` stays `false` — Escape and backdrop click close the modal,
 * which is what a dialog is expected to do. The old app disabled it on every
 * popup; form modals that really can't afford a stray dismissal opt in per call.
 */
export const modalDefaultOptions: MatDialogConfig = {
  panelClass: 'sci-modal-panel',
  backdropClass: 'sci-modal-backdrop',
  disableClose: false,
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/i18n/',
        suffix: '/translations.json',
      }),
    }),
    provideBrowserGlobalErrorListeners(),
    appRouterProviders,
    // Prescribed order: auth header -> project context -> API error.
    provideHttpClient(
      withInterceptors([authHeaderInterceptor, projectContextInterceptor, apiErrorInterceptor]),
      withXhr(),
    ),
    provideAppIcons(),
    provideEnvironmentInitializer(() => inject(ThemeService)),
    provideEnvironmentInitializer(() => inject(I18nService).init()),
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: snackbarDefaultOptions },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: modalDefaultOptions },
    // Blocks bootstrap until a stored session is rehydrated, so guards see a
    // settled auth state on the very first navigation.
    provideAppInitializer(() => inject(AuthStore).restoreSession()),
  ],
};
